import { Fragment, useMemo, useState } from "react";
import { NOTE_STATUSES, noteStatusLabel, noteStatusStyle } from "./constants";
import DateNavBar, { toLocalDate, atNoon, getWeekBounds, groupHeading } from "./DateNavBar";
import { TypeDot } from "./TorksManager";

const fmtDate = (d) => d ? new Date(d).toLocaleDateString("sv-SE") : "–";

const COL_COUNT = 6;

const NO_DATE_GROUP = "no-date";

// groupHeading gives day-level labels only; in "all" mode groups can span years.
const headingFor = (dateStr, viewMode) => {
    if (dateStr === NO_DATE_GROUP) return "Utan datum";
    const heading = groupHeading(dateStr);
    if (viewMode === "all") {
        const year = atNoon(dateStr).getFullYear();
        if (year !== new Date().getFullYear()) return `${heading} ${year}`;
    }
    return heading;
};

const toggleInSet = (set, value) => {
    const next = new Set(set);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    return next;
};

const NoteRow = ({ r, noteTypes }) => (
    <tr className="border-b hover:bg-gray-50">
        <Td><span className="font-medium">{r.torkName}</span></Td>
        <Td>
            <span className="inline-flex items-center gap-1.5">
                <TypeDot noteTypes={noteTypes} type={r.type} />
                {r.type || "–"}
            </span>
        </Td>
        <Td>
            <span className={"px-2 py-0.5 rounded-full text-xs " + noteStatusStyle(r.status)}>
                {noteStatusLabel(r.status)}
            </span>
        </Td>
        <Td>
            {r.note
                ? <span className="text-gray-700 max-w-xs block truncate" title={r.note}>{r.note}</span>
                : <span className="text-gray-400">–</span>
            }
        </Td>
        <Td>{r.author || "–"}</Td>
        <Td>
            {r.date
                ? fmtDate(r.date)
                : r.createdAt
                    ? <>{fmtDate(r.createdAt)} <span className="text-gray-400">(skapad)</span></>
                    : "–"
            }
        </Td>
    </tr>
);

/* Flat, filterable list of all notes across torks — grouped by day,
   filterable by note type and status. Read-only reporting view. */
const TorkNotesList = ({ torks, noteTypes }) => {
    const [viewMode, setViewMode] = useState("all");
    const [anchorDate, setAnchorDate] = useState(() => toLocalDate(new Date()));
    const [selectedTypes, setSelectedTypes] = useState(() => new Set());
    const [selectedStatuses, setSelectedStatuses] = useState(() => new Set());

    const today = toLocalDate(new Date());

    const allRows = useMemo(() =>
        torks.flatMap(t => (t.notes || []).map(n => ({
            ...n,
            torkId: t.id,
            torkName: t.name,
            effectiveDate: n.date || n.createdAt || null,
        }))),
        [torks]
    );

    // Union of configured type names + orphan type names still present on notes
    // (types renamed/deleted after note creation stay filterable, gray dot).
    const typeNames = useMemo(() => {
        const names = noteTypes.map(nt => nt.name);
        const known = new Set(names);
        for (const r of allRows) {
            if (r.type && !known.has(r.type)) { known.add(r.type); names.push(r.type); }
        }
        return names;
    }, [noteTypes, allRows]);

    const filtered = useMemo(() => {
        let rows = allRows.filter(r =>
            (selectedTypes.size === 0 || selectedTypes.has(r.type)) &&
            (selectedStatuses.size === 0 || selectedStatuses.has(r.status))
        );

        if (viewMode === "day") {
            rows = rows.filter(r => r.effectiveDate && toLocalDate(new Date(r.effectiveDate)) === anchorDate);
        } else if (viewMode === "week") {
            const { mon, sun } = getWeekBounds(atNoon(anchorDate));
            rows = rows.filter(r => {
                if (!r.effectiveDate) return false;
                const d = new Date(r.effectiveDate);
                return d >= mon && d <= sun;
            });
        }

        return rows.slice().sort((a, b) => {
            if (!a.effectiveDate) return 1;
            if (!b.effectiveDate) return -1;
            return new Date(b.effectiveDate) - new Date(a.effectiveDate);
        });
    }, [allRows, selectedTypes, selectedStatuses, viewMode, anchorDate]);

    // Ordered day-groups (most recent day first, matching sort order)
    const dayGroups = [];
    if (viewMode !== "day") {
        for (const r of filtered) {
            const date = r.effectiveDate ? toLocalDate(new Date(r.effectiveDate)) : NO_DATE_GROUP;
            const group = dayGroups[dayGroups.length - 1];
            if (group && group.date === date) group.rows.push(r);
            else dayGroups.push({ date, rows: [r] });
        }
    }

    return (
        <div>
            {/* Filters: note types + statuses + count */}
            <div className="flex flex-wrap items-center gap-2 mb-3">
                {typeNames.map(name => (
                    <button
                        key={name}
                        onClick={() => setSelectedTypes(prev => toggleInSet(prev, name))}
                        className={
                            "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border transition " +
                            (selectedTypes.has(name)
                                ? "bg-blue-50 border-blue-300 text-blue-800 font-medium"
                                : "bg-white border-gray-300 text-gray-600 hover:bg-gray-100")
                        }
                    >
                        <TypeDot noteTypes={noteTypes} type={name} />
                        {name}
                    </button>
                ))}
                <span className="mx-1 w-px h-5 bg-gray-300" />
                {NOTE_STATUSES.map(status => (
                    <button
                        key={status}
                        onClick={() => setSelectedStatuses(prev => toggleInSet(prev, status))}
                        className={
                            "px-3 py-1.5 rounded-full text-xs border transition " +
                            (selectedStatuses.has(status)
                                ? noteStatusStyle(status) + " border-transparent font-medium"
                                : "bg-white border-gray-300 text-gray-600 hover:bg-gray-100")
                        }
                    >
                        {noteStatusLabel(status)}
                    </button>
                ))}
                <span className="ml-auto text-sm text-gray-500">{filtered.length} noteringar</span>
            </div>

            <DateNavBar
                viewMode={viewMode}
                anchorDate={anchorDate}
                onViewModeChange={setViewMode}
                onAnchorDateChange={setAnchorDate}
            />

            {allRows.length === 0 ? (
                <p className="text-gray-500 p-4">Inga noteringar.</p>
            ) : filtered.length === 0 ? (
                <p className="text-gray-500 p-4">Inga noteringar matchar valda filter under vald period.</p>
            ) : (
                <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                        <thead className="bg-gray-100 text-gray-600 text-left">
                            <tr>
                                <Th>Tork</Th>
                                <Th>Typ</Th>
                                <Th>Status</Th>
                                <Th>Notering</Th>
                                <Th>Rapportör</Th>
                                <Th>Datum</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {viewMode !== "day"
                                ? dayGroups.map(group => (
                                    <Fragment key={group.date}>
                                        <tr>
                                            <td
                                                colSpan={COL_COUNT}
                                                className={
                                                    "px-3 py-2 text-sm font-semibold border-b " +
                                                    (group.date === today
                                                        ? "bg-blue-50 text-blue-700"
                                                        : "bg-gray-50 text-gray-700")
                                                }
                                            >
                                                {(group.date === today ? "Idag · " : "") + headingFor(group.date, viewMode)} ({group.rows.length})
                                            </td>
                                        </tr>
                                        {group.rows.map(r => (
                                            <NoteRow key={`${r.torkId}:${r.id}`} r={r} noteTypes={noteTypes} />
                                        ))}
                                    </Fragment>
                                ))
                                : filtered.map(r => (
                                    <NoteRow key={`${r.torkId}:${r.id}`} r={r} noteTypes={noteTypes} />
                                ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

const Th = ({ children }) => <th className="px-3 py-2 font-medium">{children}</th>;
const Td = ({ children }) => <td className="px-3 py-2">{children}</td>;

export default TorkNotesList;
